import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bilancioApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  TrendingUp,
  TrendingDown,
  Wallet,
  Save,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Settings,
  Plus,
  Trash2,
  Edit,
  Calculator,
  Euro,
  PiggyBank
} from 'lucide-react';

const MESI = [
  { value: 1, label: 'Gennaio' },
  { value: 2, label: 'Febbraio' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Aprile' },
  { value: 5, label: 'Maggio' },
  { value: 6, label: 'Giugno' },
  { value: 7, label: 'Luglio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Settembre' },
  { value: 10, label: 'Ottobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Dicembre' },
];

const BilancioPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const now = new Date();
  
  const [anno, setAnno] = useState(now.getFullYear());
  const [mese, setMese] = useState(now.getMonth() + 1);
  const [bilancio, setBilancio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [anniDisponibili, setAnniDisponibili] = useState([]);
  const [speseModificate, setSpeseModificate] = useState({});
  const [showSettoriDialog, setShowSettoriDialog] = useState(false);
  const [settori, setSettori] = useState([]);
  const [editingSettore, setEditingSettore] = useState(null);
  const [newSettore, setNewSettore] = useState({ nome: '', descrizione: '', ordine: 99 });

  useEffect(() => {
    loadAnniDisponibili();
  }, []);

  useEffect(() => {
    loadBilancio();
  }, [anno, mese]);

  const loadAnniDisponibili = async () => {
    try {
      const data = await bilancioApi.getAnniDisponibili();
      setAnniDisponibili(data.anni);
    } catch (error) {
      // Usa anno corrente come fallback
      setAnniDisponibili([now.getFullYear()]);
    }
  };

  const loadBilancio = async () => {
    setLoading(true);
    try {
      const data = await bilancioApi.getBilancio(anno, mese);
      setBilancio(data);
      // Inizializza le spese modificabili
      const spese = {};
      data.spese_per_settore.forEach(s => {
        if (!s.auto_calcolato) {
          spese[s.settore_id] = { importo: s.importo, note: s.note };
        }
      });
      setSpeseModificate(spese);
    } catch (error) {
      toast.error('Errore nel caricamento del bilancio');
    } finally {
      setLoading(false);
    }
  };

  const loadSettori = async () => {
    try {
      const data = await bilancioApi.getSettori();
      setSettori(data);
    } catch (error) {
      toast.error('Errore nel caricamento dei settori');
    }
  };

  const handleSpeseChange = (settoreId, field, value) => {
    setSpeseModificate(prev => ({
      ...prev,
      [settoreId]: {
        ...prev[settoreId],
        [field]: field === 'importo' ? parseFloat(value) || 0 : value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const spese = Object.entries(speseModificate).map(([settore_id, data]) => ({
        settore_id,
        importo: data.importo || 0,
        note: data.note || ''
      }));
      
      await bilancioApi.saveSpese(anno, mese, spese);
      toast.success('Spese salvate con successo!');
      loadBilancio(); // Ricarica per aggiornare i totali
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const navigateMese = (direction) => {
    let newMese = mese + direction;
    let newAnno = anno;
    
    if (newMese > 12) {
      newMese = 1;
      newAnno++;
    } else if (newMese < 1) {
      newMese = 12;
      newAnno--;
    }
    
    setMese(newMese);
    setAnno(newAnno);
  };

  const handleCreateSettore = async () => {
    try {
      await bilancioApi.createSettore(newSettore);
      toast.success('Settore creato!');
      setNewSettore({ nome: '', descrizione: '', ordine: 99 });
      loadSettori();
      loadBilancio();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleUpdateSettore = async () => {
    if (!editingSettore) return;
    try {
      await bilancioApi.updateSettore(editingSettore.id, {
        nome: editingSettore.nome,
        descrizione: editingSettore.descrizione,
        ordine: editingSettore.ordine
      });
      toast.success('Settore aggiornato!');
      setEditingSettore(null);
      loadSettori();
      loadBilancio();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDeleteSettore = async (settoreId) => {
    if (!confirm('Eliminare questo settore?')) return;
    try {
      await bilancioApi.deleteSettore(settoreId);
      toast.success('Settore eliminato!');
      loadSettori();
      loadBilancio();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      useGrouping: 'always',
    }).format(value || 0);
  };

  // Calcola totale in tempo reale
  const calcolaTotaleUscite = () => {
    if (!bilancio) return 0;
    let totale = 0;
    bilancio.spese_per_settore.forEach(s => {
      if (s.auto_calcolato) {
        totale += s.importo;
      } else {
        totale += speseModificate[s.settore_id]?.importo || 0;
      }
    });
    return totale;
  };

  const calcolaTotaleUsciteNetto = () => {
    if (!bilancio) return 0;
    let totale = 0;
    bilancio.spese_per_settore.forEach(s => {
      if (s.auto_calcolato) {
        totale += s.importo_netto ?? s.importo;
      } else {
        totale += speseModificate[s.settore_id]?.importo || 0;
      }
    });
    return totale;
  };

  const totaleUsciteCalcolato = calcolaTotaleUscite();
  const totaleUsciteNetto = calcolaTotaleUsciteNetto();
  const utilePerditaCalcolato = bilancio ? bilancio.totale_entrate - totaleUsciteCalcolato : 0;
  const utileNetto = bilancio ? bilancio.totale_entrate_netto - totaleUsciteNetto : 0;

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 px-4 py-4 border-b border-border">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/')}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold font-heading tracking-tight">Bilancio Mensile</h1>
            </div>
            {isAdmin() && (
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => { loadSettori(); setShowSettoriDialog(true); }}
                data-testid="settings-btn"
              >
                <Settings className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Month Selector */}
          <div className="flex items-center justify-between bg-secondary/50 rounded-xl p-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigateMese(-1)}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            <div className="flex items-center gap-2">
              <Select value={mese.toString()} onValueChange={(v) => setMese(parseInt(v))}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MESI.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={anno.toString()} onValueChange={(v) => setAnno(parseInt(v))}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {anniDisponibili.map(a => (
                    <SelectItem key={a} value={a.toString()}>
                      {a}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigateMese(1)}
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 max-w-lg mx-auto space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : bilancio && (
          <>
            {/* Riepilogo Card */}
            <Card className={`${utilePerditaCalcolato >= 0 ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300' : 'bg-gradient-to-br from-red-50 to-rose-50 border-red-300'}`}>
              <CardContent className="p-4">
                <div className="text-center mb-4">
                  <p className="text-sm text-muted-foreground">
                    {bilancio.mese_nome} {bilancio.anno}
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    {utilePerditaCalcolato >= 0 ? (
                      <TrendingUp className="w-8 h-8 text-green-600" />
                    ) : (
                      <TrendingDown className="w-8 h-8 text-red-600" />
                    )}
                    <span className={`text-3xl font-bold font-mono ${utilePerditaCalcolato >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(utilePerditaCalcolato)}
                    </span>
                  </div>
                  <p className={`text-sm font-medium mt-1 ${utilePerditaCalcolato >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {utilePerditaCalcolato >= 0 ? 'UTILE' : 'PERDITA'} · IVA INCLUSA
                  </p>
                  <p className="text-xs text-muted-foreground mt-1" data-testid="bilancio-utile-netto">
                    Al netto IVA: <span className="font-mono font-semibold">{formatCurrency(utileNetto)}</span>
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-xs font-medium">ENTRATE</span>
                    </div>
                    <p className="text-lg font-bold font-mono text-green-700" data-testid="bilancio-entrate">
                      {formatCurrency(bilancio.totale_entrate)}
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid="bilancio-entrate-netto">
                      netto IVA {formatCurrency(bilancio.totale_entrate_netto)}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      Venduto Cassa in Cloud · {bilancio.giornate_venduto} giornate
                    </p>
                    {bilancio.entrate_da_chiusure > 0 && (
                      <p className="text-[11px] text-muted-foreground" data-testid="bilancio-entrate-chiusure">
                        chiusure di cassa {formatCurrency(bilancio.entrate_da_chiusure)}
                      </p>
                    )}
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-red-600 mb-1">
                      <TrendingDown className="w-4 h-4" />
                      <span className="text-xs font-medium">USCITE</span>
                    </div>
                    <p className="text-lg font-bold font-mono text-red-700" data-testid="bilancio-uscite">
                      {formatCurrency(totaleUsciteCalcolato)}
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid="bilancio-uscite-netto">
                      netto IVA {formatCurrency(totaleUsciteNetto)}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {bilancio.spese_per_settore.length} settori
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {bilancio.avvisi?.length > 0 && (
              <div
                className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-1"
                data-testid="bilancio-avvisi"
              >
                {bilancio.avvisi.map((a, i) => (
                  <p key={i} className="text-[11px] text-amber-800">{a}</p>
                ))}
              </div>
            )}

            {/* Spese per Settore */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-red-600" />
                  Spese per Settore
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {bilancio.spese_per_settore.map((spesa) => (
                  <div 
                    key={spesa.settore_id}
                    className={`p-3 rounded-lg border ${spesa.auto_calcolato ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {spesa.auto_calcolato && (
                          <Calculator className="w-4 h-4 text-blue-600" />
                        )}
                        <Label className="font-medium">{spesa.settore_nome}</Label>
                      </div>
                      {spesa.auto_calcolato && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          Auto
                        </span>
                      )}
                    </div>
                    
                    {spesa.auto_calcolato ? (
                      <div>
                        <p className="text-xl font-bold font-mono text-blue-700">
                          {formatCurrency(spesa.importo)}
                        </p>
                        {spesa.importo_netto !== spesa.importo && (
                          <p className="text-xs font-mono text-blue-600">
                            netto IVA {formatCurrency(spesa.importo_netto)}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {spesa.note}
                        </p>
                        {spesa.nota_iva && (
                          <p className="text-[11px] text-muted-foreground">{spesa.nota_iva}</p>
                        )}
                      </div>
                    ) : (
                      <div className="relative">
                        <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          className="pl-9 font-mono text-right"
                          value={speseModificate[spesa.settore_id]?.importo || ''}
                          onChange={(e) => handleSpeseChange(spesa.settore_id, 'importo', e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Save Button */}
            <Button 
              className="w-full h-14 text-lg font-bold"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-bilancio-btn"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Salva Spese
                </>
              )}
            </Button>
          </>
        )}
      </main>

      {/* Dialog Gestione Settori */}
      <Dialog open={showSettoriDialog} onOpenChange={setShowSettoriDialog}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gestione Settori di Spesa</DialogTitle>
            <DialogDescription>
              Modifica i nomi dei settori o aggiungine di nuovi
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Lista settori esistenti */}
            {settori.map((settore) => (
              <div key={settore.id} className="flex items-center gap-2 p-2 border rounded-lg">
                {editingSettore?.id === settore.id ? (
                  <div className="flex-1 space-y-2">
                    <Input
                      value={editingSettore.nome}
                      onChange={(e) => setEditingSettore({...editingSettore, nome: e.target.value})}
                      placeholder="Nome settore"
                    />
                    <Input
                      value={editingSettore.descrizione}
                      onChange={(e) => setEditingSettore({...editingSettore, descrizione: e.target.value})}
                      placeholder="Descrizione"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleUpdateSettore}>Salva</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingSettore(null)}>Annulla</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex-1">
                      <p className="font-medium">{settore.nome}</p>
                      <p className="text-xs text-muted-foreground">{settore.descrizione}</p>
                      {settore.auto_calcolo && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          Auto-calcolato
                        </span>
                      )}
                    </div>
                    {!settore.auto_calcolo && (
                      <div className="flex gap-1">
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={() => setEditingSettore({...settore})}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={() => handleDeleteSettore(settore.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}

            {/* Aggiungi nuovo settore */}
            <div className="pt-4 border-t">
              <p className="text-sm font-medium mb-2">Aggiungi nuovo settore</p>
              <div className="space-y-2">
                <Input
                  value={newSettore.nome}
                  onChange={(e) => setNewSettore({...newSettore, nome: e.target.value})}
                  placeholder="Nome settore"
                />
                <Input
                  value={newSettore.descrizione}
                  onChange={(e) => setNewSettore({...newSettore, descrizione: e.target.value})}
                  placeholder="Descrizione (opzionale)"
                />
                <Button 
                  className="w-full"
                  onClick={handleCreateSettore}
                  disabled={!newSettore.nome}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Aggiungi Settore
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettoriDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BilancioPage;
